/**
 * Funnel progress tracking, so an abandoned form still reaches the CRM.
 *
 * Fire-and-forget by design: every call swallows its own errors and returns nothing. Analytics
 * must never be able to break the funnel it is measuring — a failed track call has to be
 * invisible to the person filling the form in.
 *
 * The session id lives in sessionStorage, not localStorage: it should survive a reload and a
 * back-button, but a visit next week is a new attempt, not a continuation of an old one.
 */

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;
const KEY = 'levr_form_session';
/** Where the hero calculator parks its numbers between screens. Per attempt, like the session. */
export const AMOUNTS_KEY = 'levr_hero_amounts';
const DEALER_KEY = 'levr_dealer_code';
const ENTRY_KEY = 'levr_entry_page';
const SOURCE_KEY = 'levr_traffic_source';

function newId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `s_${crypto.randomUUID()}`;
  } catch {
    /* fall through */
  }
  return `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function getSessionId() {
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = newId();
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    // Private mode / storage disabled — a per-load id still records the drop-off point, it just
    // won't survive a reload. Better than tracking nothing.
    return newId();
  }
}

/** Start a fresh session — call after a successful submit so the next visitor isn't merged in. */
export function resetSession() {
  try {
    sessionStorage.removeItem(KEY);
    // Otherwise the next visit in this tab reopens on the previous person's price and deposit —
    // on a shared device that exposes what they were looking at, and invites a lead submitted with
    // figures nobody chose.
    sessionStorage.removeItem(AMOUNTS_KEY);
    // Attribution ends with the attempt it belongs to. Left behind, the next lead in this tab
    // would be credited to a dealer who had nothing to do with it.
    sessionStorage.removeItem(DEALER_KEY);
    sessionStorage.removeItem(ENTRY_KEY);
    sessionStorage.removeItem(SOURCE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * The referral for this visit.
 *
 * Held in sessionStorage rather than threaded through navigation state. Attribution has to survive
 * every route into the funnel — the hero, the navbar, the footer, the calculator page, the apply
 * page, and the round trip back from the quick-start — and a scheme where each link has to remember
 * to pass it along is one forgotten link away from an unpaid commission. Storing it once means every
 * entry point gets it for free.
 *
 * Scope is deliberate: sessionStorage dies with the tab, and `resetSession` clears it the moment a
 * lead is submitted. So a referral lasts for one browsing session, up to one submitted lead — the
 * standard session-attribution window, not forever.
 */
export function readDealerCode() {
  let fromUrl = '';
  try {
    fromUrl = new URLSearchParams(window.location.search).get('dealer') || '';
  } catch {
    /* no window/search — fall through to storage */
  }
  try {
    if (fromUrl) sessionStorage.setItem(DEALER_KEY, fromUrl);
    return fromUrl || sessionStorage.getItem(DEALER_KEY) || '';
  } catch {
    return fromUrl;
  }
}

/**
 * The page this visit STARTED on.
 *
 * This is what answers "which landing page is actually producing leads" — the question the whole
 * vertical-pages exercise exists to answer, and one the funnel cannot answer after the fact,
 * because by the time someone submits they are on `/quick-start` regardless of where they came in.
 *
 * FIRST touch wins, and is never overwritten. Someone who lands on /finance/jdm, reads the home
 * page, then converts is a lead that /finance/jdm earned; crediting the last page before the form
 * would credit the home page for traffic it did not bring. That is the standard attribution choice
 * and the one worth being explicit about, because the numbers mean nothing without it.
 *
 * Same scope as the dealer referral: sessionStorage, so it dies with the tab, and `resetSession`
 * clears it once a lead is submitted. One browsing session, up to one lead.
 */
const NOT_A_LANDING_PAGE = [
  '/login', '/register', '/auth', '/dashboard', '/dealer', '/admin', '/analytics',
  '/privacy', '/terms', '/disclaimer',
  // Retired, and redirected to /calculator — but `useCaptureAttribution()` runs before the
  // <Navigate> does, so without this the old URL would still claim first-touch attribution and the
  // CRM would keep reporting leads against a page that no longer exists.
  '/compare',
];

export function readEntryPage() {
  try {
    const existing = sessionStorage.getItem(ENTRY_KEY);
    if (existing) return existing;
  } catch {
    return '';
  }

  let path = '/';
  try {
    path = window.location.pathname || '/';
  } catch {
    /* no window — leave the default */
  }

  // Auth, account and legal routes are skipped rather than recorded, so the FIRST MARKETING page
  // wins. Someone who follows a password-reset link, or opens the privacy policy from an email,
  // and only then browses to /finance/jdm and converts is a lead that /finance/jdm produced —
  // crediting "/login" (or bucketing it as "other") would bury the page that actually did the work
  // and make the comparison useless. Nothing is stored until a real landing page is seen, so a
  // later navigation still gets the chance to claim it.
  if (NOT_A_LANDING_PAGE.some((p) => path === p || path.startsWith(`${p}/`))) return '';

  try {
    sessionStorage.setItem(ENTRY_KEY, path);
  } catch {
    /* storage unavailable; the value is still returned for this render */
  }
  return path;
}

/**
 * The traffic source this visit arrived with: the `utm_source` / `utm_medium` / `utm_campaign` on
 * its landing URL, e.g. Importify's NZ finance page links here with `utm_source=importifycars`.
 *
 * FIRST TOUCH, like the entry page: the first URL in this tab that carries a `utm_source` wins, and
 * later navigations never overwrite it, so a lead is credited to whoever actually sent the visitor
 * rather than to an internal link. Same scope too: sessionStorage, cleared by `resetSession` once a
 * lead is submitted. A visit with no tags records nothing, and the lead arrives as untagged.
 *
 * The server normalises and bounds every value (this is public input that ends up grouped on a
 * dashboard), so this only reads and remembers.
 */
export function readTrafficSource() {
  try {
    const existing = sessionStorage.getItem(SOURCE_KEY);
    if (existing) return JSON.parse(existing);
  } catch {
    return {};
  }
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return {};
  }
  const source = params.get('utm_source');
  if (!source) return {};
  // Bounded here too, though the server is what guarantees a bad tag can never refuse a lead.
  const tag = (v) => (v ? String(v).slice(0, 100) : null);
  const tags = {
    utm_source: tag(source),
    utm_medium: tag(params.get('utm_medium')),
    utm_campaign: tag(params.get('utm_campaign')),
  };
  try {
    sessionStorage.setItem(SOURCE_KEY, JSON.stringify(tags));
  } catch {
    /* storage unavailable; the value is still returned for this render */
  }
  return tags;
}

/** The traffic-source fields to send with a lead: always all three keys, null when absent. */
export function trafficSourceFields() {
  const t = readTrafficSource() || {};
  return {
    utm_source: t.utm_source || null,
    utm_medium: t.utm_medium || null,
    utm_campaign: t.utm_campaign || null,
  };
}

/**
 * Capture the referral from whatever URL the visitor is on, on every navigation.
 *
 * Mounted once at the app root and re-run on each route change, so a client-side navigation to a
 * referred URL is captured as well as a hard load. Reading it per-page meant `/calculator?dealer=X`
 * and `/apply?dealer=X` never captured it at all, and every CTA into the funnel had to remember to
 * pass it along — six of them didn't.
 *
 * Takes the current path as an argument rather than importing the router, so this module stays
 * free of routing dependencies and testable on its own.
 */
export function useCaptureAttribution(React, locationKey) {
  const { useEffect } = React;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    readDealerCode();
    // Runs on every navigation but only ever writes once — see `readEntryPage`. Calling it here
    // rather than in a landing page component is deliberate: a visitor who arrives on ANY page is
    // attributable, and a per-page call is one forgotten page away from a blank.
    readEntryPage();
    // Same reasoning: a visitor tagged by a partner's link can land on any page.
    readTrafficSource();
  }, [locationKey]);
}

/** @deprecated Use `useCaptureAttribution` — kept so an older import cannot silently no-op. */
export const useCaptureReferral = useCaptureAttribution;

/**
 * Record progress. `capture` is 'hero' or 'full'; `payload` is whatever the form holds so far.
 * Contact fields are sent separately so the backend can store them as columns and match a contact.
 */
export function trackStep({ capture, step, stepKey, totalSteps, form = {}, payload = {} }) {
  const body = {
    session_id: getSessionId(),
    capture,
    step,
    step_key: stepKey,
    total_steps: totalSteps,
    name: form.name || null,
    email: form.email || null,
    phone: form.phone || null,
    consent: Boolean(form.consent),
    dealer_code: form.dealer_code || null,
    // Read here rather than taken from `form`: the hero calculator's form has no entry_page, and
    // an abandoned calculator session is exactly the case worth attributing to a landing page.
    // The server keeps the FIRST value, so sending it on every step is safe.
    entry_page: readEntryPage() || null,
    payload,
  };

  try {
    const json = JSON.stringify(body);
    // keepalive lets the request survive the page being closed — which is exactly the moment we
    // most want the record, since that IS the abandonment.
    fetch(`${API}/forms/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never let tracking break the funnel */
  }
}

/** Mark the session converted so the abandonment sweep skips it. */
export function trackConverted(applicationId) {
  try {
    fetch(`${API}/forms/session/converted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: getSessionId(), application_id: applicationId }),
      keepalive: true,
    })
      .catch(() => {})
      .finally(() => resetSession());
  } catch {
    resetSession();
  }
}

/**
 * Track a funnel from a component, from the first sign the person is actually filling it in.
 *
 * Nothing is sent until they engage (see `hasEngaged`) — loading the page is not starting the
 * form. After that, the three moments that matter:
 *
 *  1. step changes — the progress signal;
 *  2. a debounced write after typing stops — otherwise details entered on the LAST step are never
 *     sent, because no step change follows them. That's the most valuable data we have, since
 *     it's the contact details of someone who then walked away;
 *  3. `pagehide` — a final beacon at the exact moment of abandonment.
 *
 * `pagehide` is used rather than `beforeunload`, which is unreliable on mobile Safari, and the
 * request is sent with keepalive so it survives the page going away.
 */
export function useFormSessionTracking(
  React,
  { capture, step, stepKey, totalSteps, form, engagedFromStart = false },
) {
  const { useEffect, useRef } = React;
  const latest = useRef({ capture, step, stepKey, totalSteps, form });
  latest.current = { capture, step, stepKey, totalSteps, form };

  // Where this form started, captured once. Anything different from this means they touched it.
  const start = useRef({ step, form: JSON.stringify(form) });
  // `engagedFromStart` is for a screen you can only reach by already having started — the
  // quick-start form, which is only reachable by pressing Continue on the calculator. There the
  // gate would be wrong: arriving is itself the engagement, and waiting for a further change means
  // someone who opens it and stops is never recorded, which is exactly the drop-off worth seeing.
  const engaged = useRef(Boolean(engagedFromStart));

  /**
   * Has this person actually started the form, as opposed to merely loading the page it sits on?
   *
   * All three effects below fire on mount, and `pagehide` fires for anyone leaving the page — so
   * without this gate every visitor to the landing page became a form session and, 20 minutes
   * later, an "abandoned form" in the CRM. That buried the real drop-outs in a list of bounces
   * (none of them reachable, since a passer-by leaves no contact details), pinned the drop-off
   * chart at ~90% on step 1 forever, and made the completion rate a measure of homepage bounce.
   *
   * Latches, and is evaluated the moment something changes rather than when a send is due. Judging
   * it only at send time meant a value typed and then reverted before the 1500ms debounce elapsed
   * looked identical to never having touched the form, so a genuine start went unrecorded.
   */
  const hasEngaged = () => {
    if (engaged.current) return true;
    const s = latest.current;
    if (s.step > start.current.step || JSON.stringify(s.form) !== start.current.form) {
      engaged.current = true;
    }
    return engaged.current;
  };

  // 1. Step changes.
  useEffect(() => {
    if (!hasEngaged()) return;
    const s = latest.current;
    trackStep({ ...s, payload: s.form });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // 2. Debounced after typing stops.
  useEffect(() => {
    // Latch synchronously, while this value is the current one — see `hasEngaged`.
    hasEngaged();
    const t = setTimeout(() => {
      if (!engaged.current) return;
      const s = latest.current;
      trackStep({ ...s, payload: s.form });
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // 3. The moment they leave.
  useEffect(() => {
    const onHide = () => {
      if (!engaged.current) return;
      const s = latest.current;
      trackStep({ ...s, payload: s.form });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
